import { DraftEditorCommand, EditorState, RichUtils } from 'draft-js';
import { KidsData } from '../editor/KidsEditor';
import { Editor } from '@tiptap/core';
import { DOMSerializer } from '@tiptap/pm/model';
import type { PdfElement } from '../../kidsV2/types/KidPdfGeneratorV2.types';
import { TEXT_LAYOUT_PRESETS } from '../../kidsV2/components/wrapper/BlockInputWrapper';

// Sanitise the text from the editor to fit the expected format for the PDF generator
export const sanitiseEditorText = (originalText: string) => {
  // Replace specific HTML tags to fit the expected format for the PDF generator
  const replacedText = originalText
    .replaceAll('<p>', '')
    .replaceAll('</p>', '')
    .replaceAll('<strong>', '<b>')
    .replaceAll('</strong>', '</b>')
    .replaceAll('<em>', '<i>')
    .replaceAll('</em>', '</i>')
    .replaceAll('<s>', '<strike>')
    .replaceAll('</s>', '</strike>');

  // 'NFC' is a normalisation form for canonical composition => replace the unicode characters
  const normalizedString = replacedText.normalize('NFC');

  // Remove <br> or <br/> tags (line breaks) and replace them with a space
  const sanitisedText = normalizedString
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/\n/g, ' ')
    .replace(/&nbsp;/g, ' ') // Remove non-breaking space entities
    .trimEnd(); // Remove trailing spaces at the end

  return sanitisedText;
};

// Clean up malformed HTML by fixing tag structure, closing unclosed tags, and removing orphaned tags
// I honestly think we shouldn't have this kind of logic in the editor utils, but it's here for now
// COULD CAUSE SIDE EFFECTS
const fixMalformedHTML = (html: string): string => {
  if (!html) return html;

  // Self-closing tags that don't need closing
  const selfClosingTags = ['br', 'hr', 'img', 'input', 'meta', 'link'];

  // Tags we care about
  const tagNames = [
    'mark',
    'font',
    'span',
    'b',
    'strong',
    'i',
    'em',
    'u',
    'strike',
    's',
  ];

  // Find all tags (opening and closing) with their positions
  const tagRegex = /<\/?([a-zA-Z][a-zA-Z0-9]*)[^>]*>/g;
  const tagMatches: Array<{
    index: number;
    endIndex: number;
    match: string;
    name: string;
    isClosing: boolean;
  }> = [];

  let match;
  while ((match = tagRegex.exec(html)) !== null) {
    const tagName = match[1].toLowerCase();
    if (!selfClosingTags.includes(tagName) && tagNames.includes(tagName)) {
      tagMatches.push({
        index: match.index,
        endIndex: match.index + match[0].length,
        match: match[0],
        name: tagName,
        isClosing: match[0].startsWith('</'),
      });
    }
  }

  // Use a stack to track opened tags
  const stack: Array<{ name: string; index: number }> = [];
  const resultParts: Array<string> = [];
  let lastIndex = 0;

  // Process each tag match
  for (const tagMatch of tagMatches) {
    // Add text before this tag
    resultParts.push(html.substring(lastIndex, tagMatch.index));

    if (tagMatch.isClosing) {
      // Find matching opening tag in stack (LIFO - Last In First Out)
      let found = false;
      for (let i = stack.length - 1; i >= 0; i--) {
        if (stack[i].name === tagMatch.name) {
          // Found matching tag - close all tags that were opened after it first
          for (let j = stack.length - 1; j > i; j--) {
            resultParts.push(`</${stack[j].name}>`);
          }
          // Close the matching tag
          resultParts.push(tagMatch.match);
          // Remove matched tag and all tags after it from stack
          stack.splice(i);
          found = true;
          break;
        }
      }

      if (!found) {
        // Orphaned closing tag - skip it (don't add to result)
      }
    } else {
      // Opening tag - add to stack and to result
      stack.push({ name: tagMatch.name, index: tagMatch.index });
      resultParts.push(tagMatch.match);
    }

    lastIndex = tagMatch.endIndex;
  }

  // Add remaining text
  resultParts.push(html.substring(lastIndex));

  // Close any remaining unclosed tags at the end (in reverse order)
  for (let i = stack.length - 1; i >= 0; i--) {
    resultParts.push(`</${stack[i].name}>`);
  }

  let result = resultParts.join('');

  // Additional cleanup: remove empty tags
  result = result.replace(/<span[^>]*>\s*<\/font>/gi, '');
  result = result.replace(/<span[^>]*><\/font>/gi, '');
  result = result.replace(/<font[^>]*>\s*<\/font>/gi, '');
  result = result.replace(/<font[^>]*><\/font>/gi, '');
  result = result.replace(/<mark[^>]*>\s*<\/mark>/gi, '');
  result = result.replace(/<mark[^>]*><\/mark>/gi, '');
  result = result.replace(/<b[^>]*>\s*<\/b>/gi, '');
  result = result.replace(/<b[^>]*><\/b>/gi, '');

  return result;
};

// Strip unsupported attributes from HTML elements
// ReportLab paraparser only supports specific attributes, so we need to remove:
// - rel, class, data-*, style attributes from links
// - class, style, data-* from all elements (except where we convert style to supported attributes)
const stripUnsupportedAttributes = (html: string): string => {
  if (!html) return html;

  // List of supported attributes for <a> tags in ReportLab paraparser
  const supportedLinkAttributes = [
    'href',
    'target',
    'underline',
    'underlineColor',
    'underlineGap',
    'underlineKind',
    'underlineOffset',
    'underlineWidth',
    'color',
    'backColor',
    'fontName',
    'fontSize',
    'name',
    'dest',
    'destination',
    'link',
  ];

  // Strip unsupported attributes from <a> tags
  html = html.replace(/<a\s+([^>]*)>/gi, (match, attributes) => {
    if (!attributes) return '<a>';

    // Parse attributes - handle both quoted and unquoted values
    const attrMap = new Map<string, string>();
    // Match attribute="value" or attribute='value'
    const attrRegex = /(\w+(?:-\w+)*)\s*=\s*["']([^"']*)["']/gi;
    let attrMatch;

    while ((attrMatch = attrRegex.exec(attributes)) !== null) {
      const attrName = attrMatch[1].toLowerCase();
      const attrValue = attrMatch[2];

      // Only keep supported attributes
      if (
        supportedLinkAttributes.some(
          (supported) => supported.toLowerCase() === attrName,
        )
      ) {
        attrMap.set(attrName, attrValue);
      }
    }

    // Rebuild the tag with only supported attributes
    if (attrMap.size === 0) return '<a>';
    const cleanAttrs = Array.from(attrMap.entries())
      .map(([key, value]) => `${key}="${value}"`)
      .join(' ');
    return `<a ${cleanAttrs}>`;
  });

  // Strip class, style, data-*, rel, and other unsupported attributes from all other elements
  // Note: We'll handle style->color/backColor conversion in the main sanitize function
  html = html.replace(/<(\w+)([^>]*)>/gi, (match, tagName, attributes) => {
    const tagLower = tagName.toLowerCase();
    if (tagLower === 'a' || !attributes) return match;

    // Remove unsupported attributes: class, rel, data-*, style (we'll extract color/backColor separately)
    // Keep only basic attributes that might be needed
    const cleanAttrs = attributes
      .replace(/\s*(?:class|rel|data-\w+|style)\s*=\s*["'][^"']*["']/gi, '')
      .trim();

    return cleanAttrs ? `<${tagName} ${cleanAttrs}>` : `<${tagName}>`;
  });

  return html;
};

// Sanitise TipTap editor HTML: normalizes colors to hex, converts TipTap tags to PDF format, then sanitizes
export const sanitiseEditorTipTap = (originalText: string) => {
  let html = originalText;
  const ignoredInheritedColorValues = new Set([
    'inherit',
    'initial',
    'unset',
    'revert',
    'revert-layer',
    'currentcolor',
  ]);

  // --- 0️⃣ Fix malformed HTML first ---
  html = fixMalformedHTML(html);

  // --- 0.5️⃣ Extract color/backColor from style attributes BEFORE stripping them ---
  // Convert <span style="color: ..."> to <span color="...">
  html = html.replace(
    /<(\w+)([^>]*)\s+style=["']([^"']*color:\s*([^;"']+)[^"']*)["']([^>]*)>/gi,
    (match, tagName, beforeStyle, styleContent, colorValue, afterStyle) => {
      const normalizedColorValue = colorValue.trim().toLowerCase();
      if (ignoredInheritedColorValues.has(normalizedColorValue)) {
        // Ignore non-explicit CSS color keywords so highlight markup doesn't force color="inherit".
        const cleanAttrs = `${beforeStyle}${afterStyle}`.trim();
        return cleanAttrs ? `<${tagName} ${cleanAttrs}>` : `<${tagName}>`;
      }

      // Extract color value and add as attribute
      const colorAttr = `color="${colorValue.trim()}"`;
      // Remove the style attribute from the tag
      const cleanAttrs = `${beforeStyle}${afterStyle}`.trim();
      return cleanAttrs
        ? `<${tagName} ${colorAttr} ${cleanAttrs}>`
        : `<${tagName} ${colorAttr}>`;
    },
  );

  // Convert background-color from style or data-color to backColor attribute
  html = html.replace(
    /<(\w+)([^>]*)\s+(?:style=["'][^"']*background-color:\s*([^;"']+)[^"']*["']|data-color=["']([^"']+)["'])([^>]*)>/gi,
    (
      match,
      tagName,
      beforeAttr,
      bgColorFromStyle,
      bgColorFromData,
      afterAttr,
    ) => {
      const bgColor = (bgColorFromStyle || bgColorFromData || '').trim();
      if (!bgColor) return match;
      const backColorAttr = `backColor="${bgColor}"`;
      const cleanAttrs = `${beforeAttr}${afterAttr}`.trim();
      return cleanAttrs
        ? `<${tagName} ${backColorAttr} ${cleanAttrs}>`
        : `<${tagName} ${backColorAttr}>`;
    },
  );

  // --- 0.6️⃣ Strip unsupported attributes (rel, class, data-*, style) from all elements ---
  html = stripUnsupportedAttributes(html);

  // --- 1️⃣ Convert rgb(...) → #hex ---
  html = html.replace(
    /rgb\((\d+),\s*(\d+),\s*(\d+)\)/g,
    (_, r, g, b) =>
      '#' +
      [r, g, b]
        .map((x) => {
          const hex = parseInt(x, 10).toString(16);
          return hex.length === 1 ? '0' + hex : hex;
        })
        .join(''),
  );

  // --- 2️⃣ Convert <span color="..."> → <font color="..."> ---
  html = html.replace(
    /<span\s+color=["']([^"']+)["']([^>]*)>/gi,
    '<font color="$1"$2>',
  );
  html = html.replace(
    /<span\s+backColor=["']([^"']+)["']([^>]*)>/gi,
    '<font backColor="$1"$2>',
  );
  html = html.replace(/<span([^>]*)>/gi, '<font$1>');

  // --- 3️⃣ Replace </span> with </font> ---
  html = html.replace(/<\/span>/gi, '</font>');

  // --- 4️⃣ Convert <mark backColor="..."> → <font backColor="..."> ---
  html = html.replace(
    /<mark\s+backColor=["']([^"']+)["']([^>]*)>/gi,
    '<font backColor="$1"$2>',
  );
  html = html.replace(/<mark([^>]*)>/gi, '<font$1>');

  // --- 5️⃣ Replace </mark> with </font> ---
  html = html.replace(/<\/mark>/gi, '</font>');

  // --- 6️⃣ Final cleanup: remove any remaining orphaned closing tags ---
  html = fixMalformedHTML(html);

  // Now apply the standard sanitization
  return sanitiseEditorText(html);
};

// Reusable function for handling keyboard shortcuts for styling
export const handleEditorKeyCommand = (
  command: DraftEditorCommand,
  editorState: EditorState,
  setEditorState: (state: EditorState) => void,
): 'handled' | 'not-handled' => {
  // If the command is 'split-block' (the Enter key), prevent the line break
  if (command === 'split-block') {
    return 'handled'; // Prevent line break from being inserted
  }

  const newState = RichUtils.handleKeyCommand(editorState, command);
  if (newState) {
    setEditorState(newState);
    return 'handled';
  }
  return 'not-handled';
};

// Function to swap the positions of two elements in an array
export function swapFields(data: KidsData[], index1: number, index2: number) {
  const tempElement = { ...data[index1] };
  data[index1] = { ...data[index2], kiidIndex: index1 };
  data[index2] = { ...tempElement, kiidIndex: index2 };
}

//Re-indexes the `kiidIndex` property of each item in the array starting from a specified index.
export const reIndexKiidContent = (content: KidsData[]): KidsData[] => {
  return content.map((item, index) => ({
    ...item,
    kiidIndex: index,
  }));
};

export function checkSelectionHasFormat(
  selectedHTML: string,
  format: 'bold' | 'italic' | 'underline' | 'strike',
): boolean {
  if (!selectedHTML) return false;

  const formatMap = {
    // Ensure the tag name ends before a space or '>' to avoid matching like <strong> for <s>
    bold: /<(strong|b)(?=[\s>])[^>]*>/i,
    italic: /<(em|i)(?=[\s>])[^>]*>/i,
    underline: /<u(?=[\s>])[^>]*>/i,
    strike: /<(?:(?:del)|(?:strike)|s)(?=[\s>])[^>]*>/i,
  };

  return formatMap[format].test(selectedHTML);
}

export function getSelectedTextColor(selectedHTML: string): string {
  if (!selectedHTML) return '#000000';

  const styleMatches = selectedHTML.matchAll(/style=["']([^"']*)["']/gi);
  for (const styleMatch of styleMatches) {
    const declarations = styleMatch[1].split(';');

    for (const declaration of declarations) {
      const [property, rawValue] = declaration.split(':');
      if (!property || !rawValue) continue;

      if (property.trim().toLowerCase() === 'color') {
        const color = rawValue.trim();
        if (color) return color;
      }
    }
  }

  return '#000000';
}

export function getSelectedHTML(editor: Editor): string {
  if (!editor) return '';
  const { state } = editor.view;
  const { from, to } = state.selection;

  if (from === to) return ''; // No selection

  const fragment = state.doc.slice(from, to).content;
  const serializer = DOMSerializer.fromSchema(editor.schema);
  const tempDiv = document.createElement('div');

  fragment.forEach((node) => {
    const dom = serializer.serializeNode(node);
    tempDiv.appendChild(dom);
  });

  return tempDiv.innerHTML;
}

type ElementGroup = {
  elements: Array<{ field: PdfElement; index: number }>;
  layout: number[] | null;
};

// This function groups consecutive elements with matching fixed_width layouts
export function groupElementsByLayout(elements: PdfElement[]): ElementGroup[] {
  const groups: ElementGroup[] = [];

  let currentGroup: ElementGroup | null = null;

  const arraysMatch = (
    arr1: number[] | undefined,
    arr2: number[] | undefined,
  ) => {
    if (!arr1 || !arr2) return false;
    if (arr1.length !== arr2.length) return false;
    return arr1.every((val, idx) => val === arr2[idx]);
  };

  elements.forEach((field, index) => {
    const layout = field.layout?.fixed_width;

    if (!currentGroup) {
      // Start a new group
      currentGroup = {
        elements: [{ field, index }],
        layout: layout || null,
      };
    } else if (arraysMatch(currentGroup.layout as number[], layout)) {
      // Add to current group if layouts match
      currentGroup.elements.push({ field, index });
    } else {
      // Save current group and start new one
      groups.push(currentGroup);
      currentGroup = {
        elements: [{ field, index }],
        layout: layout || null,
      };
    }
  });

  // Don't forget the last group
  if (currentGroup) {
    groups.push(currentGroup);
  }

  return groups;
}

export function getLayoutPresetName(
  layout: number[] | null | undefined,
): string {
  if (!layout || layout.length === 0) {
    // Check for Full preset
    const fullPreset = TEXT_LAYOUT_PRESETS.find((p) => !p.layout);
    return fullPreset?.name || 'Full Width';
  }

  const arraysEqual = (a: number[], b: number[]) => {
    if (a.length !== b.length) return false;
    return a.every((val, idx) => val === b[idx]);
  };

  // Find matching preset from TEXT_LAYOUT_PRESETS
  const matchingPreset = TEXT_LAYOUT_PRESETS.find((preset) => {
    const presetFixed = preset.layout?.fixed_width;
    if (!presetFixed) return false;
    return arraysEqual(layout, presetFixed);
  });

  if (matchingPreset) {
    return matchingPreset.name;
  }

  // Check for custom ratio preset
  const customPreset = TEXT_LAYOUT_PRESETS.find((p) => p.id === 'custom-ratio');
  return customPreset?.name || 'Custom Layout';
}
